import { useCallback, useMemo, useState } from 'react';
import { MenuItem, Menu, Typography, Box, MenuProps } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { QuickLinks } from '../communications-chat-box';
import { SubmenuSearch } from './submenu-search';
import React from 'react';
import { styled } from '@mui/system';
import QuickActionsService, { FormLink } from '../../../../../services/quick-actions-service/quick-actions-service';
import { useConfig } from '../../../../../../../../libs/logic/config/src/hooks/config/config';
import { useOidcWithAdmin } from '../../../../../../../../libs/logic/oidc/src/hooks/use-oidc-with-admin-intercept';

const MenuHighlighted = styled(MenuItem)(({ theme }) => ({
  background: 'rgba(0, 0, 0, 0.08)'
}));


const BackMenuItemStyled = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  padding: '8px 16px',
  height: 48
}));

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 16px',
  height: 48
}));

const FormsMenu = styled(Box)(({ theme }) => ({
  height: 300,
  minWidth: 320,
  display: 'grid',
  gridTemplateRows: '1fr min-content',
  gridTemplateAreas: `'formsItems' 'formsSearch'`
}));

const FormsItems = styled(Box)(({ theme }) => ({
  gridArea: 'formsItems',
  overflowY: 'scroll'
}));

const FormsSearch = styled(Box)(({ theme }) => ({
  gridArea: 'formsSearch',
  borderTop: '1px solid #E9EAEE',
  paddingTop: 11
}));

const NoScroll = styled(Menu)(({ theme }) => ({
  overflow: 'hidden'
}));

interface FormsSubmenuProps {
  quickLinks: QuickLinks;
  onSelect: (val: string) => void;
  locationId?: number;
  patientId?: number;
  isForms2?: boolean;
}


enum View {
  FormsOrPackets,
  Forms,
  Packets,
}

export const FormsSubmenu = ({ quickLinks, onSelect, locationId, patientId, isForms2 }: FormsSubmenuProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [searchText, setSearchText] = useState('');
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const quickActions = QuickActionsService.getInstance(config, oidcService);
  const baseView = useMemo(() => isForms2 ? View.FormsOrPackets : View.Forms, [isForms2]);
  const [currentView, setCurrentView] = useState<View>(baseView);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSearchText('');
    setCurrentView(baseView);
  }, []);

  const handleSelectLink = async (formLink: FormLink) => {
    if (isForms2 && locationId && patientId) {
      formLink.link = await quickActions.getAssignedFormOrPacketLink(
        locationId,
        patientId,
        formLink.type === 'form' ? formLink.id.toString() : '',
        formLink.type === 'form' ? '' : formLink.id.toString()
      )
    }

    const link = formLink.link || '';
    onSelect(link);
    handleMenuClose();
  };

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setCurrentView(baseView);
  }, []);

  const filteredForms = useMemo(() => {
    if (isForms2) {
      return quickLinks.formLinks.filter(
        (link) => link.id !== null && link.type == 'form' && link.name?.toLowerCase().includes(searchText.toLowerCase())
      );
    } else {
      return quickLinks.formLinks.filter(
        (link) => link.name?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

  }, [quickLinks.formLinks, searchText]);

  const filteredPackets = useMemo(() => {
    return quickLinks.formLinks.filter(
      (link) => link.id !== null && link.type == 'packet' && link.name?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [quickLinks.formLinks, searchText]);

  if (!quickLinks.formLinks.length) return null;

  const renderMenuContent = () => {
    switch (currentView) {
      case View.FormsOrPackets:
        return (
          <>
            <BackMenuItemStyled onClick={() => handleMenuClose()}>
              <ChevronLeftIcon />
              <Typography variant="body1">&#8288;Close</Typography>
            </BackMenuItemStyled>
            <MenuItemStyled onClick={() => setCurrentView(View.Forms)}>
              <Typography variant="body1">&#8288;Forms</Typography>
              <ChevronRightIcon />
            </ MenuItemStyled>
            <MenuItemStyled onClick={() => setCurrentView(View.Packets)}>
              <Typography variant="body1">&#8288;Packets</Typography>
              <ChevronRightIcon />
            </MenuItemStyled>
          </>
        );
      case View.Forms:
        return (
          <>
            <BackMenuItemStyled onClick={() => isForms2 ? setCurrentView(View.FormsOrPackets) : handleMenuClose()}>
              <ChevronLeftIcon />
              <Typography variant="body1">&#8288;Back</Typography>
            </BackMenuItemStyled>
            {filteredForms.map((link) => (
              <MenuItemStyled key={link.id} onClick={() => handleSelectLink(link)}>
                <Typography variant="body1">&#8288;{link.name}</Typography>
              </MenuItemStyled>
            ))}
            <FormsSearch>
              <SubmenuSearch searchText={searchText} onChange={e => setSearchText(e.target.value)} />
            </FormsSearch>
          </>
        );
      case View.Packets:
        return (
          <>
            <BackMenuItemStyled onClick={() => setCurrentView(View.FormsOrPackets)}>
              <ChevronLeftIcon />
              <Typography variant="body1">&#8288;Back</Typography>
            </BackMenuItemStyled>
            {filteredPackets.map((link) => (
              <MenuItemStyled key={link.id} onClick={() => handleSelectLink(link)}>
                <Typography variant="body1">&#8288;{link.name}</Typography>
              </MenuItemStyled>
            ))}
            <FormsSearch>
              <SubmenuSearch searchText={searchText} onChange={e => setSearchText(e.target.value)} />
            </FormsSearch>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <MenuItemStyled
        data-testid='unified-inbox-forms-quick-action'
        sx={{ ...(Boolean(menuAnchorEl) && { background: 'rgba(0, 0, 0, 0.08)' }) }}
        onClick={handleMenuOpen}
      >
        <Typography variant='body1'>&#8288;Insert Forms Link</Typography>
        <ChevronRightIcon />
      </MenuItemStyled>
      <NoScroll
        id='unified-inbox-forms-quick-action-menu'
        data-testid='unified-inbox-forms-quick-action-menu'
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        PaperProps={{ sx: { backgroundColor: '#FFFFFF' } }}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <FormsMenu>
          <FormsItems>
            {renderMenuContent()}
          </FormsItems>
        </FormsMenu>
      </NoScroll>
    </>
  );

}


